import { LitElement, html, svg, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { assetUrl } from '../assets.js';
import { fetchWeather, weatherCategory, type WeatherData, type WeatherCategory } from '../weather.js';

/**
 * Ekran başlığı — solda logo + tenant/şube adı, sağda opsiyonel saat/tarih/hava
 * durumu (ayrı kart gerekmez). Hava durumu Open-Meteo ile GERÇEK (weather.ts);
 * weather_city başlık config'inden gelir.
 *
 * Logo render: assetUrl(id) helper → public API endpoint.
 */
@customElement('fp-header')
export class FpHeader extends LitElement {
    @property({ type: String, attribute: 'title-source' }) titleSource:
        | 'tenant_name'
        | 'branch_name'
        | 'custom' = 'tenant_name';
    @property({ type: String, attribute: 'title-custom' }) titleCustom = '';
    @property({ type: String, attribute: 'logo-asset' }) logoAsset: string | null = null;
    @property({ type: String, attribute: 'logo-position' }) logoPosition: 'left' | 'center' | 'right' = 'left';
    @property({ type: Array }) widgets: string[] = [];
    @property({ type: String, attribute: 'weather-city' }) weatherCity = '';

    @state() private now: Date = new Date();
    @state() private weather: WeatherData | null = null;
    private clockTimer?: number;
    private weatherTimer?: number;

    createRenderRoot(): HTMLElement {
        return this;
    }

    connectedCallback(): void {
        super.connectedCallback();
        if (this.widgets.includes('clock') || this.widgets.includes('date')) {
            // Saat dakika hassasiyetinde (HH:MM) — saniye gösterimi TV'de gereksiz titreşim +
            // CPU (L4). 15sn'de bir yenile (dakika değişimini en geç 15sn'de yakalar).
            this.clockTimer = window.setInterval(() => {
                this.now = new Date();
            }, 15_000);
        }
        if (this.widgets.includes('weather') && this.weatherCity) {
            void this.loadWeather();
            this.weatherTimer = window.setInterval(() => void this.loadWeather(), 20 * 60 * 1000);
        }
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        if (this.clockTimer !== undefined) clearInterval(this.clockTimer);
        if (this.weatherTimer !== undefined) clearInterval(this.weatherTimer);
        this.clockTimer = undefined;
        this.weatherTimer = undefined;
    }

    private async loadWeather(): Promise<void> {
        this.weather = await fetchWeather(this.weatherCity);
    }

    private resolveTitle(): string {
        if (this.titleSource === 'custom') return this.titleCustom || '';
        const ctx = (window as unknown as { fpContext?: { tenant_name?: string; branch_name?: string } }).fpContext;
        if (this.titleSource === 'tenant_name') return ctx?.tenant_name ?? 'FiyatPanom';
        if (this.titleSource === 'branch_name') return ctx?.branch_name ?? 'Merkez';
        return 'FiyatPanom';
    }

    /** Hava durumu ikonu (inline SVG, emoji yok; currentColor ile temaya uyar). */
    private weatherSvg(cat: WeatherCategory): TemplateResult {
        const s = (paths: TemplateResult) => svg`
            <svg viewBox="0 0 24 24" width="1.1em" height="1.1em" fill="none"
                 stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                 style="vertical-align:-0.15em;">${paths}</svg>`;
        switch (cat) {
            case 'clear':
                return s(svg`<circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M2 12h2M20 12h2M5 5l1.5 1.5M17.5 17.5L19 19M19 5l-1.5 1.5M6.5 17.5L5 19"/>`);
            case 'cloud':
                return s(svg`<path d="M17 18a4 4 0 0 0 0-8 6 6 0 0 0-11.5 2A3.5 3.5 0 0 0 6 18z"/>`);
            case 'rain':
                return s(svg`<path d="M17 14a4 4 0 0 0 0-8 6 6 0 0 0-11.5 2A3.5 3.5 0 0 0 6 14z"/><path d="M8 18l-1 2M12 18l-1 2M16 18l-1 2"/>`);
            case 'snow':
                return s(svg`<path d="M17 13a4 4 0 0 0 0-8 6 6 0 0 0-11.5 2A3.5 3.5 0 0 0 6 13z"/><path d="M8 18h.01M12 19h.01M16 18h.01"/>`);
            case 'storm':
                return s(svg`<path d="M17 13a4 4 0 0 0 0-8 6 6 0 0 0-11.5 2A3.5 3.5 0 0 0 6 13z"/><path d="M12 13l-2 4h3l-2 4"/>`);
            case 'fog':
                return s(svg`<path d="M4 9h16M4 13h16M6 17h12"/>`);
        }
    }

    render() {
        const title = this.resolveTitle();
        const hasWidgets =
            this.widgets.includes('clock') ||
            this.widgets.includes('date') ||
            this.widgets.includes('weather');

        const justify = hasWidgets
            ? 'justify-between'
            : this.logoPosition === 'center' ? 'justify-center'
            : this.logoPosition === 'right' ? 'justify-end'
            : 'justify-start';

        return html`
            <div class="flex items-center gap-4 px-6 py-3 ${justify}" data-fp-logo
                 style="background: var(--fp-surface, var(--fp-color-surface)); color: var(--fp-color-fg);
                        border-bottom: 1px solid color-mix(in srgb, var(--fp-color-fg-muted) 20%, transparent);">
                <div class="flex items-center gap-4" style="min-width:0;">
                    ${this.logoAsset ? html`<img src="${assetUrl(this.logoAsset)}" alt=""
                        class="h-10 w-auto" style="flex:0 0 auto;max-width:30vw;object-fit:contain;"
                        @error=${(e: Event) => { (e.target as HTMLElement).style.display = 'none'; }}>` : ''}
                    <h1 class="text-2xl font-semibold tracking-wide" style="white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${title}</h1>
                </div>
                ${hasWidgets ? html`
                    <div class="flex items-center gap-5" style="flex:0 0 auto;">
                        ${this.widgets.includes('clock') ? html`
                            <span class="text-2xl tabular-nums" style="font-family: var(--fp-font-numeric);">
                                ${this.now.toLocaleTimeString('tr-TR', { timeZone: 'Europe/Istanbul', hour: '2-digit', minute: '2-digit' })}
                            </span>` : ''}
                        ${this.widgets.includes('date') ? html`
                            <span class="text-base" style="color: var(--fp-color-fg-muted);">
                                ${this.now.toLocaleDateString('tr-TR', { timeZone: 'Europe/Istanbul', weekday: 'long', day: 'numeric', month: 'long' })}
                            </span>` : ''}
                        ${this.widgets.includes('weather') && this.weather ? html`
                            <span class="text-xl d-inline-flex" style="display:inline-flex;align-items:center;gap:.35em;">
                                ${this.weatherSvg(weatherCategory(this.weather.code))}
                                <span class="tabular-nums">${this.weather.temp}°C</span>
                            </span>` : ''}
                    </div>
                ` : ''}
            </div>
        `;
    }
}
